import { SelectedItem } from "01Data/02User/Locations";
import { AppContext } from "01Data/AppData";
import { BuildingInfo } from "01Data/DataTypes";
import PageTemplate from "04UserPages/00PageTemplate/PageTemplate";
import {
  BreadCrumbItem,
  BreadCrumbList,
} from "04UserPages/99Parts/BreadCrumbList";
import {
  IFDlgCaller,
  RegisterFormBuilding,
} from "04UserPages/99Parts/CommonDlgs";
import { ConfirmDialog } from "04UserPages/99Parts/ConfirmDialog";
import { MapReader } from "97Parts/MapReader";
import { NoteText } from "97Parts/Styles";
import { useModel, useSignal } from "99Tools/useModel";
import {
  AddCircle,
  Cancel,
  CheckCircle,
  Delete,
  Edit,
  OpenInNew,
} from "@mui/icons-material";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  styled,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import { LIST_HEADER_COLOR } from "Settings";
import { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router";

const ColorHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: LIST_HEADER_COLOR,
  color: theme.palette.common.white,
}));

const FormDlg = styled("form")(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%", // Fix IE 11 issue.
}));

const ErrorText = styled("div")(({ theme }) => ({
  color: "#f00",
}));

const ButtonDlg = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

const DetailForm = () => {
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pageLocations;
  const b = p.selected.building;
  const [bShowConfirmDelete, setShowConfirmDelete] = useState(false);
  const redraw = useSignal();

  const handleClose = () => {
    p.list.bShowDetailDlg = false;
    b.clear();
    p.changed();
  };

  const handleDelete = () => {
    d.DeleteLocation()
      .then(() => {
        p.list.bShowDetailDlg = false;
        d.GetLocationData()
          .then(() => {
            p.changed();
          })
          .catch((err: any) => {
            b.setError(err, "建物情報取得に失敗しました。");
            p.changed();
          });
        setShowConfirmDelete(false);
      })
      .catch((err: any) => {
        b.setError(err, "建物削除に失敗しました。");
        setShowConfirmDelete(false);
        p.changed();
      });
  };

  const handleChangeName = (e: any) => {
    b.BuildingName = e.target.value;
    b.errorMessage = "";
    redraw();
  };

  const handleChangeAddress = (e: any) => {
    b.BuildingAddress = e.target.value;
    b.errorMessage = "";
    redraw();
  };

  const handleApply = () => {
    if (b.BuildingName === "") {
      b.errorMessage = "建物名を入力してください。";
      redraw();
      return;
    }
    d.EditLocation()
      .then(() => {
        p.list.bShowDetailDlg = false;
        b.setFileName("");
        d.GetLocationData()
          .then(() => {
            p.changed();
          })
          .catch((err: any) => {
            b.setError(err, "建物情報取得に失敗しました。");
            p.changed();
          });
      })
      .catch((err: any) => {
        b.setError(err, "建物設定変更に失敗しました。");
        p.changed();
      });
  };

  if (!p.selected.IsSelected()) {
    return null;
  }

  return (
    <>
      <Dialog key="detail" open={p.list.bShowDetailDlg}>
        <FormDlg noValidate>
          <DialogTitle>詳細情報編集</DialogTitle>
          <DialogContent>
            <NoteText>付帯情報</NoteText>
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              required
              id={String("名前")}
              label={"名前"}
              name="name"
              inputProps={{ maxLength: 128 }}
              value={p.selected.building.BuildingName}
              onChange={handleChangeName}
            />
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              id={String("住所")}
              label={"住所"}
              name="address"
              inputProps={{ maxLength: 128 }}
              value={p.selected.building.BuildingAddress}
              onChange={handleChangeAddress}
            />
            <MapReader entity={b} redraw={redraw} />
            {b.errorMessage !== "" ? (
              <ErrorText>{b.errorMessage}</ErrorText>
            ) : null}
            <NoteText>建物削除</NoteText>
            <DialogActions>
              <ButtonDlg
                startIcon={<Delete />}
                variant="contained"
                color="secondary"
                onClick={() => setShowConfirmDelete(true)}
              >
                建物削除
              </ButtonDlg>
            </DialogActions>
            <Divider variant="middle" />
            <DialogActions>
              <ButtonDlg
                startIcon={<CheckCircle />}
                variant="contained"
                color="primary"
                onClick={handleApply}
              >
                適用
              </ButtonDlg>
              <ButtonDlg
                startIcon={<Cancel />}
                variant="contained"
                color="primary"
                onClick={handleClose}
              >
                キャンセル
              </ButtonDlg>
            </DialogActions>
          </DialogContent>
        </FormDlg>
      </Dialog>
      <ConfirmDialog
        bShow={bShowConfirmDelete}
        title={`${p.selected.buildingPrev.BuildingName}を削除しますか？`}
        description="建物に属するデバイス情報も削除されます。"
        onApply={handleDelete}
        onCancel={() => setShowConfirmDelete(false)}
        titleButtonApply="削除"
        titleButtonCancel="キャンセル"
      />
    </>
  );
};

/**
 * カラム設定
 */
const columns = [
  { id: "name", label: "名前", minWidthPC: 100, minWidthSmartphone: "25vw" },
  { id: "map", label: "詳細場所", minWidthPC: 100, minWidthSmartphone: "10vw" },
  { id: "edit", label: "", minWidthPC: 100, minWidthSmartphone: "10vw" },
];

/**
 * 建物一覧
 * @returns TSX
 */
const LocationsBody = (props: any) => {
  const a = useContext(AppContext).account;
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pageLocations;
  const navigate = useNavigate();

  const handleDetailDlgOpen = (i: number) => {
    if (a.IsUserAdmin() || a.IsUserSubAdmin()) {
      p.list.bShowDetailDlg = true;
      p.selected = new SelectedItem(i, d.locations[i]);
      p.changed();
    }
  };

  const handleDeviceEdit = (i: number) => {
    d.ResetPageData(); // ページデータをリセット
    navigate(`/devices?building=${d.locations[i].BuildingId}`);
  };

  const handleShowMap = (url: string) => {
    window.open(url, "_blank");
  };

  const rows = d.locations
    .sort((a, b) =>
      props.sortInfo.order === "asc"
        ? a.BuildingName.localeCompare(b.BuildingName)
        : b.BuildingName.localeCompare(a.BuildingName)
    )
    .slice(
      p.list.page * p.list.rowsPerPage,
      p.list.page * p.list.rowsPerPage + p.list.rowsPerPage
    )
    .map((row: BuildingInfo, i: number) => {
      const index = p.list.page * p.list.rowsPerPage + i;
      return (
        <TableRow hover role="button" tabIndex={-1} key={row.BuildingId}>
          <TableCell
            key={columns[0].id + i}
            align="center"
            sx={
              a.IsUserAdmin() || a.IsUserSubAdmin()
                ? { cursor: "pointer" }
                : null
            }
            onClick={() => handleDetailDlgOpen(index)}
          >
            {row.BuildingName}
          </TableCell>
          <TableCell key={columns[1].id + i} align="center">
            {row.BuildingImage === "" ? (
              <Button
                endIcon={<OpenInNew />}
                size="small"
                variant="contained"
                disabled
                sx={{ textWrap: "nowrap" }}
              >
                表示
              </Button>
            ) : (
              <Button
                endIcon={<OpenInNew />}
                size="small"
                variant="contained"
                onClick={() => handleShowMap(row.BuildingImage)}
                sx={{ textWrap: "nowrap" }}
              >
                表示
              </Button>
            )}
          </TableCell>
          <TableCell
            key={columns[2].id + i}
            align="center"
            style={{ whiteSpace: "nowrap" }}
          >
            <Button
              startIcon={<Edit />}
              size="small"
              variant="contained"
              onClick={() => handleDeviceEdit(index)}
            >
              デバイス編集
            </Button>
          </TableCell>
        </TableRow>
      );
    });

  return <TableBody>{rows}</TableBody>;
};
/**
 * 建物テーブル
 * @returns TSX
 */
const LocationTable = () => {
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pageLocations;

  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    p.list.page = newPage;
    p.changed();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    p.list.rowsPerPage = +event.target.value;
    p.list.page = 0;
    p.changed();
  };

  const [sortInfo, setSortInfo] = useState({ target: "名前", order: "asc" });
  const handleChangeSortOrder = (target: any) => {
    if (target !== "名前") {
      return;
    }

    if (sortInfo.order === "asc") {
      setSortInfo({ target: target, order: "desc" });
    } else {
      setSortInfo({ target: target, order: "asc" });
    }
  };

  const GetSortExp = (label: string, order: string) => {
    if (label !== sortInfo.target) {
      return "";
    }
    switch (order) {
      case "asc":
        return " ▲";
      case "desc":
        return " ▼";
      default:
        return "";
    }
  };

  const header = columns.map((column) => (
    <ColorHeader
      key={column.id}
      align="center"
      style={{
        minWidth: isSmartphone ? column.minWidthSmartphone : column.minWidthPC,
      }}
      onClick={() => handleChangeSortOrder(column.label)}
    >
      {column.label + GetSortExp(column.label, sortInfo.order)}
    </ColorHeader>
  ));

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ minHeight: "55vh" }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead style={{ whiteSpace: "nowrap" }}>
            <TableRow>{header}</TableRow>
          </TableHead>
          <LocationsBody sortInfo={sortInfo} />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={d.locations.length}
        rowsPerPage={p.list.rowsPerPage}
        page={p.list.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

const LocationContents = () => {
  const a = useContext(AppContext).account;
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pageLocations;
  useModel(p);

  useEffect(() => {
    d.GetLocationData()
      .then(() => {
        p.changed();
      })
      .catch((err: any) => {
        d.SetError(err, "建物情報取得に失敗しました。");
        p.changed();
      });
  }, [d, p]);

  const handleRegisterDlgOpen = () => {
    p.bShowRegisterDlg = true;
    p.changed();
  };

  const caller: IFDlgCaller = {
    bShow: () => p.bShowRegisterDlg,
    close: () => {
      p.bShowRegisterDlg = false;
      p.changed();
    },
    redraw: () => p.changed(),
    action: async (data: any) => {
      await d.RegisterLocation(
        data.name,
        data.syokenId,
        data.address,
        data.image,
        data.imageExtension
      );
      await d.GetLocationData();
      p.changed();
    },
  };

  return (
    <>
      {
        // 権限チェック
        a.IsUserManager() || a.IsUserViewer() ? (
          <div style={{ marginTop: "16px" }} /> // スペーサー
        ) : (
          <ButtonDlg
            variant="contained"
            color="primary"
            onClick={handleRegisterDlgOpen}
            startIcon={<AddCircle />}
          >
            新規追加
          </ButtonDlg>
        )
      }
      <LocationTable />
      <RegisterFormBuilding caller={caller} />
      <DetailForm />
    </>
  );
};

const LocationPage = () => {
  return (
    <PageTemplate title="建物一覧">
      <BreadCrumbList
        items={[
          new BreadCrumbItem("TOP", "/top/"),
          new BreadCrumbItem("建物一覧"),
        ]}
      />
      <LocationContents />
    </PageTemplate>
  );
};

export default LocationPage;
